import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, RefreshCw, Copy } from "lucide-react";

const LOG_LABEL = { access_on: "Access on", access_off: "Access off", suspend: "Suspended", reactivate: "Reactivated", approve: "Approved" };

export default function AffiliatesAdmin() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/admin/affiliate-accounts").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/affiliate-accounts/sync"); toast.success(`Checked ${data.checked} affiliates · ${data.created} new accounts`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setBusy(false); }
  };
  const act = async (a, action) => {
    if (action === "reset" && !window.confirm(`Email a set-password link to ${a.email}?`)) return;
    let reason = "";
    if (action === "suspend" || action === "access_off") {
      const r = window.prompt(`Private reason for pausing ${a.name || a.email} (team only, optional):`, "");
      if (r === null) return;
      reason = r;
    }
    try { await api.post(`/admin/affiliate-accounts/${a.user_id}/${action}`, { reason }); toast.success(action === "reset" ? `Sign-in email sent to ${a.email}` : action === "access_on" ? "Dashboard access turned on" : action === "access_off" ? "Dashboard access turned off" : "Updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { load(); }
  };
  if (!d) return null;
  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-4" data-testid="affiliates-admin">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-lg font-bold text-sam-navy">Affiliate accounts ({d.affiliates.length})</h3>
          <p className="text-xs text-sam-slate">New affiliate sign-ups get dashboard access right away, plus a set-password email. Existing affiliates found by sync get an account; send their sign-in email when you're ready. Turning access off or suspending pauses sign-in only — referral links and commissions keep tracking.</p>
        </div>
        <Button size="sm" onClick={sync} disabled={busy} className="bg-sam-navy" data-testid="affiliates-sync">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Sync sign-ups now</Button>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-sam-slate" data-testid="affiliates-webhook">
        Instant sign-up webhook: <code className="truncate">{d.webhook_url}</code> · header <code>X-Webhook-Secret</code>
        <button onClick={() => { navigator.clipboard?.writeText(d.webhook_secret); toast.success("Secret copied"); }} data-testid="affiliates-copy-secret"><Copy className="h-3.5 w-3.5" /></button>
      </div>
      <div className="mt-3 divide-y divide-border">
        {d.affiliates.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`aff-row-${a.user_id}`}>
            <div className="min-w-0 flex-1">
              <b className="text-sam-navy">{a.name || a.email}</b> {a.suspended ? <Badge className="bg-sam-crimson" data-testid={`aff-status-${a.user_id}`}>Suspended</Badge> : !a.dashboard_access ? <Badge className="bg-sam-amber" data-testid={`aff-status-${a.user_id}`}>Access off</Badge> : <Badge className="bg-sam-green" data-testid={`aff-status-${a.user_id}`}>Active</Badge>}
              <div className="text-xs text-sam-slate">{a.email} · code {a.code || "—"} · ZIPs {(a.zips || []).join(", ") || "none"} · joined {a.created_at}
                {a.welcome_email && <span className={a.welcome_email.ok ? " text-sam-green" : " text-sam-crimson"} data-testid={`aff-welcome-${a.user_id}`}> · sign-in email {a.welcome_email.ok ? "sent" : a.welcome_email.ok === false ? `failed: ${a.welcome_email.error}` : "not sent yet"}</span>}
              </div>
              {a.pause_reason && <div className="text-xs text-sam-crimson" data-testid={`aff-reason-${a.user_id}`}>Paused because: {a.pause_reason}</div>}
              {a.access_log?.length > 0 && (
                <div className="text-[11px] text-sam-slate" data-testid={`aff-log-${a.user_id}`}>
                  {a.access_log.map((l) => `${LOG_LABEL[l.action] || l.action} by ${l.by_name || l.by} ${new Date(l.at).toLocaleString()}${l.reason ? ` (${l.reason})` : ""}`).join(" · ")}
                </div>
              )}
            </div>
            {a.role !== "admin" && (
              <label className="flex items-center gap-1.5 text-xs text-sam-slate">
                Dashboard
                <Switch checked={a.dashboard_access && !a.suspended} disabled={a.suspended} onCheckedChange={(v) => act(a, v ? "access_on" : "access_off")} data-testid={`aff-access-toggle-${a.user_id}`} />
              </label>
            )}
            {a.role !== "admin" && (a.suspended
              ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(a, "reactivate")} data-testid={`aff-reactivate-${a.user_id}`}>Approve / reactivate</Button>
              : <Button size="sm" variant="outline" className="h-7 text-xs text-sam-crimson" onClick={() => act(a, "suspend")} data-testid={`aff-suspend-${a.user_id}`}>Suspend</Button>)}
            {a.role !== "admin" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(a, "reset")} data-testid={`aff-reset-${a.user_id}`}>Send sign-in / reset</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
